import React, { useState } from 'react';
import { useActivity } from '../App';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const ContentModal = ({ content, onClose, onSave }) => {
  const isEdit = !!content;
  const [form, setForm] = useState(content || {
    title: '', type: 'Blog', status: 'draft', body: '',
  });

  const handle = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  return (
    <div style={overlayS}>
      <div style={{ ...modalS, maxWidth: '580px' }}>
        <div style={modalHeaderS}>
          <h3 style={{ fontWeight: 700 }}>{isEdit ? '✏️ Edit Content' : '➕ New Content'}</h3>
          <button style={closeBtnS} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <label style={labelS}>Title *</label>
              <input style={inputS} value={form.title} onChange={handle('title')} placeholder="Content title…" />
            </div>
            <div>
              <label style={labelS}>Content Type</label>
              <select style={inputS} value={form.type} onChange={handle('type')}>
                {['Blog','Page','Banner','Campaign','Email Template','FAQ'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: '14px' }}>
            <label style={labelS}>Body / Content</label>
            <textarea style={{ ...inputS, minHeight: '120px', resize: 'vertical' }} value={form.body} onChange={handle('body')}
              placeholder="Write your content here…" />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelS}>Status</label>
            <div style={{ display: 'flex', gap: '16px' }}>
              {['draft','published','archived'].map(s => (
                <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.875rem' }}>
                  <input type="radio" name="status" value={s} checked={form.status === s}
                    onChange={() => setForm(p => ({ ...p, status: s }))} />
                  {s.charAt(0).toUpperCase()+s.slice(1)}
                </label>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button style={cancelBtnS} onClick={onClose}>Cancel</button>
            <button style={primaryBtnS} onClick={() => {
              if (!form.title.trim()) { toast.error('Title is required'); return; }
              onSave(form);
            }}>
              {form.status === 'published' ? '🌐 Publish' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TYPE_COLORS = {
  Blog: '#6366f1', Page: '#06b6d4', Banner: '#f59e0b',
  Campaign: '#22c55e', 'Email Template': '#8b5cf6', FAQ: '#ec4899',
};

const ContentManagement = () => {
  const { addLog } = useActivity();
  const { user } = useAuth();

  const [items, setItems]         = useState([]);
  const [modal, setModal]         = useState(null);
  const [search, setSearch]       = useState('');
  const [filterType, setType]     = useState('all');
  const [filterStatus, setStatus] = useState('all');

  const filtered = items.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.title.toLowerCase().includes(q) || (c.author || '').toLowerCase().includes(q);
    const matchType   = filterType === 'all'   || c.type === filterType;
    const matchStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchSearch && matchType && matchStatus;
  });

  const handleSave = (form) => {
    if (modal === 'new') {
      const newItem = {
        ...form, id: Date.now(),
        author: user?.name || 'Admin',
        updatedAt: new Date().toISOString().split('T')[0],
        views: 0,
      };
      setItems(p => [newItem, ...p]);
      addLog(form.status === 'published' ? 'Content Published' : 'Content Created', form.title, form.status === 'published' ? 'publish' : 'create');
      toast.success(form.status === 'published' ? 'Content published!' : 'Content saved as draft!');
    } else {
      setItems(p => p.map(c => c.id === modal.id
        ? { ...c, ...form, updatedAt: new Date().toISOString().split('T')[0] }
        : c
      ));
      addLog('Content Updated', form.title, 'update');
      toast.success('Content updated!');
    }
    setModal(null);
  };

  const handleDelete = (c) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    setItems(p => p.filter(x => x.id !== c.id));
    addLog('Content Deleted', c.title, 'delete');
    toast.success('Content deleted');
  };

  const handleTogglePublish = (c) => {
    const newStatus = c.status === 'published' ? 'draft' : 'published';
    setItems(p => p.map(x => x.id === c.id ? { ...x, status: newStatus } : x));
    addLog(newStatus === 'published' ? 'Content Published' : 'Content Unpublished', c.title, 'publish');
    toast.success(newStatus === 'published' ? 'Published!' : 'Unpublished');
  };

  const stats = {
    total:     items.length,
    published: items.filter(c => c.status === 'published').length,
    draft:     items.filter(c => c.status === 'draft').length,
    archived:  items.filter(c => c.status === 'archived').length,
  };

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>Content</span> / <span className="current">CMS</span></div>
          <h1>Content Management</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Create and manage blogs, banners, pages and campaigns</p>
        </div>
        <button style={primaryBtnS} onClick={() => setModal('new')}>➕ New Content</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '24px' }}>
        {[
          { label: 'Total Items',  value: stats.total,     color: 'var(--accent-primary)', icon: '📋' },
          { label: 'Published',    value: stats.published, color: '#22c55e',               icon: '🌐' },
          { label: 'Drafts',       value: stats.draft,     color: '#f59e0b',               icon: '📝' },
          { label: 'Archived',     value: stats.archived,  color: 'var(--text-muted)',     icon: '📦' },
        ].map((s, i) => (
          <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '18px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem' }}>{s.icon}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: s.color, marginTop: '4px' }}>{s.value}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '0.85rem' }}>🔍</span>
          <input style={{ ...inputS, paddingLeft: '32px' }} placeholder="Search content…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select style={{ ...inputS, width: 'auto' }} value={filterType} onChange={e => setType(e.target.value)}>
          <option value="all">All Types</option>
          {['Blog','Page','Banner','Campaign','Email Template','FAQ'].map(t => <option key={t}>{t}</option>)}
        </select>
        <select style={{ ...inputS, width: 'auto' }} value={filterStatus} onChange={e => setStatus(e.target.value)}>
          <option value="all">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>
        <span style={{ padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '0.82rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {filtered.length} items
        </span>
      </div>

      {/* Table */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                {['Title','Type','Author','Updated','Views','Status','Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Actions' ? 'right' : 'left', fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
                  {items.length === 0
                    ? '📋 No content yet. Click "New Content" to create your first piece.'
                    : 'No content matches your filters.'}
                </td></tr>
              ) : filtered.map(c => {
                const tc = TYPE_COLORS[c.type] || '#6366f1';
                return (
                  <tr key={c.id}
                    style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '11px 14px', fontWeight: 600, fontSize: '0.88rem' }}>{c.title}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ fontSize: '0.72rem', background: tc+'20', color: tc, padding: '2px 8px', borderRadius: '20px', fontWeight: 500 }}>{c.type}</span>
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{c.author}</td>
                    <td style={{ padding: '11px 14px', fontSize: '0.82rem', color: 'var(--text-muted)' }}>{c.updatedAt}</td>
                    <td style={{ padding: '11px 14px', fontWeight: 600, fontSize: '0.85rem' }}>{(c.views || 0).toLocaleString()}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{
                        fontSize: '0.72rem', padding: '3px 8px', borderRadius: '6px', fontWeight: 600,
                        background: c.status === 'published' ? 'rgba(34,197,94,0.12)' : c.status === 'archived' ? 'rgba(148,163,184,0.12)' : 'rgba(245,158,11,0.12)',
                        color:      c.status === 'published' ? '#22c55e'               : c.status === 'archived' ? '#94a3b8'                  : '#f59e0b',
                      }}>
                        {c.status === 'published' ? '🌐' : c.status === 'archived' ? '📦' : '📝'} {c.status}
                      </span>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                        <button style={actionBtnS} onClick={() => setModal(c)}>✏️ Edit</button>
                        <button
                          style={{ ...actionBtnS, color: c.status === 'published' ? '#f59e0b' : '#22c55e' }}
                          onClick={() => handleTogglePublish(c)}>
                          {c.status === 'published' ? 'Unpublish' : '🌐 Publish'}
                        </button>
                        <button style={{ ...actionBtnS, color: '#ef4444' }} onClick={() => handleDelete(c)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modal !== null && (
        <ContentModal content={modal === 'new' ? null : modal} onClose={() => setModal(null)} onSave={handleSave} />
      )}
    </div>
  );
};

const overlayS    = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' };
const modalS      = { background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '16px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' };
const modalHeaderS= { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--border-color)' };
const closeBtnS   = { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.2rem' };
const inputS      = { padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none', width: '100%', boxSizing: 'border-box' };
const labelS      = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' };
const primaryBtnS = { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', display: 'inline-flex', alignItems: 'center', gap: '6px' };
const cancelBtnS  = { padding: '9px 18px', borderRadius: '8px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.88rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
const actionBtnS  = { padding: '5px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.78rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };

export default ContentManagement;
